import { Injectable, inject } from '@angular/core';
import type { CmdkItem } from '../../models/cmdk.model';
import type { CmdkProvider, CmdkProviderContext } from '../../models/cmdk-provider';
import { CmdkRecentsService } from '../cmdk-recents.service';
import type { CmdkRecentRecord } from '../cmdk-recents.service';

/**
 * RESPONSIBILITY: the "Recent" block — what the viewer opened from the palette
 * last, rebuilt as real rows.
 *
 * Local and synchronous: the store is localStorage, so this is a memory read.
 * It does NOT extend `CmdkRemoteProviderBase`, which exists for the network.
 *
 * `idle: 'browse'` — this is half the reason recents are worth building. An
 * empty palette that shows six static suggested actions is the same surface
 * every morning; one that shows the three rooms you actually use is not.
 *
 * BACKEND CONTRACT: none.
 *
 * AI CONTEXT — the `scope` lie, and why it is deliberate
 *
 * Every other provider answers to exactly one chip. Recents span four kinds,
 * and there is no "Recent" chip in `CMDK_SCOPES`. Declaring `scope: 'all'`
 * makes `shouldRunProvider` run this only under the All chip and never under a
 * narrowing one — which is the behaviour we want: picking "Spaces" means "show
 * me spaces", not "show me spaces plus four things I opened yesterday".
 *
 * Registered LAST in `CmdkSearchService.providers`. The gather de-duplicates on
 * `kind:id` and first writer wins, so a recent that is also a live result loses
 * to the live one — which is right, because the live one has today's
 * availability and this one deliberately has none.
 */
@Injectable({ providedIn: 'root' })
export class CmdkRecentsProvider implements CmdkProvider {
  private readonly recents = inject(CmdkRecentsService);

  readonly id = 'recents';
  // The rows are of four different kinds; `kind` here only feeds `kindGroup`
  // for the block header, and every row carries its own real kind.
  readonly kind = 'nav' as const;
  readonly scope = 'all' as const;
  readonly minQueryLength = 0;
  readonly idle = 'browse' as const;

  isEnabled(): boolean {
    return true;
  }

  browse(): CmdkItem[] {
    return this.items();
  }

  /**
   * With a query, recents are still offered — they are the results most likely
   * to be wanted, and the ranker scores them against the same haystack as
   * everything else. They just rarely win, because a live match on the same
   * thing outranks and de-duplicates them.
   */
  search(_ctx: CmdkProviderContext): CmdkItem[] {
    return this.items();
  }

  private items(): CmdkItem[] {
    return this.recents.records().map(toItem);
  }
}

/**
 * Rebuild a row from its stored shadow.
 *
 * Every volatile field is `unknown` on purpose — see `CmdkRecentsService`. A
 * space rebuilt from a week-old record must not claim to be free, and the row
 * components already render `unknown` as "show no tag", so this needs no
 * special-casing downstream.
 */
function toItem(record: CmdkRecentRecord): CmdkItem {
  switch (record.kind) {
    case 'space':
      return {
        group: 'recent',
        kind: 'space',
        id: record.id,
        title: record.title,
        subtitle: record.subtitle,
        // Not stored: it is a display noun and the row does not render it in
        // the subtitle, so a stale one would only mislead the preview.
        spaceType: '',
        floorName: record.floorName,
        availability: 'unknown',
      };
    case 'person':
      return {
        group: 'recent',
        kind: 'person',
        id: record.id,
        title: record.title,
        subtitle: record.subtitle,
        presence: 'unknown',
      };
    case 'app':
      return {
        group: 'recent',
        kind: 'app',
        id: record.id,
        title: record.title,
        subtitle: record.subtitle,
        url: record.url ?? '',
      };
    case 'news':
      return {
        group: 'recent',
        kind: 'news',
        id: record.id,
        title: record.title,
        subtitle: record.subtitle,
        newsType: record.newsType ?? 'article',
        workspaceId: record.workspaceId,
      };
  }
}
